import { type BulkRequestRecord, useBulkRequestForceBusy } from "api/clients/useBulkRequestQuery";
import { captureException } from "@sentry/react";
import { useBulkRequest } from "api/clients/useBulkRequest";
import { useCallback } from "react";
import type { BulkRequestItem } from "api/requests";

/** Hook wrapper for submitting data to a bulk operation. */
export function useBulkRequestSubmission(onSubmitted: (() => void) | undefined) {
    const { isPending, mutateAsync, draftId } = useBulkRequest();

    const forceBusy = useBulkRequestForceBusy();

    const mutateWrap = useCallback(
        async (rows: BulkRequestRecord[]) => {
            const requestItems = rows.map(prepareSubmissionItem);
            try {
                // call submission API via mutation
                await mutateAsync(requestItems);
            } catch (exc) {
                const generatedError = new Error("Bulk Request submission failed", { cause: exc });
                captureException(generatedError);
                throw generatedError;
            }

            // populate react-query cache with an intermediate result indicating that the system is busy
            forceBusy();

            // switch tabs and show a status modal (generally)
            if (onSubmitted) onSubmitted();
        },
        [forceBusy, mutateAsync, onSubmitted],
    );

    return { isSubmitting: isPending, mutate: mutateWrap, draftId } as const;
}

/** Convert a Bulk Request record to the data required to submit a request. */
function prepareSubmissionItem(row: BulkRequestRecord): BulkRequestItem {
    return {
        patientId: row.pmsId,
        amount: row.amount,
        requestChannel: row.requestChannel,
        phone: row.phone,
        email: row.email,
    };
}
